import { Component, EventEmitter, HostBinding, Input, OnInit, Output, inject } from '@angular/core';
import { Router } from '@angular/router';

import { MyBetsConfig, SitecoreMyBetsConfig } from '@frontend/sports/common/client-config-data-access';
import { ActiveModal } from '@frontend/sports/modal/feature';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { BetslipListType, MyBetsImage, MyBetsImages } from '@frontend/sports/types/models/my-bets';
import { DeviceService, ProductHomepagesConfig } from '@frontend/vanilla/core';

import { MyBetsListContext } from '../../../my-bets-base/models/my-bets-viewmodels';
import { TrackNavArea } from '../../../navigation/navigation-tracking.model';
import { PopupManager } from '../../../popup/popup-manager.service';
import { MyBetsFilterConstraint } from '../../models/my-bets-filter-constraints';

@Component({
    selector: 'ms-my-bets-empty-list',
    templateUrl: 'my-bets-empty-list.component.html',
    styleUrl: 'my-bets-empty-button.scss',
    standalone: false,
})
export class MyBetsEmptyListComponent implements OnInit {
    @Input() listType?: BetslipListType;
    @Input() filterConstraint: MyBetsFilterConstraint;
    @Input() inColumn = false;
    @Input() errorLoadingBets?: boolean;
    @Input() showMigrationTabs = false;
    @Input() myBetsListContext: MyBetsListContext;

    @Output() filterChanged: EventEmitter<MyBetsFilterConstraint> = new EventEmitter<MyBetsFilterConstraint>();
    @HostBinding('class.migrated-user') isMigratedUser = false;

    isMobile: boolean;
    isTablet: boolean;
    emptyBetsImg = this.sitecoreMyBets.myBetsImages.find((image: MyBetsImage) => image.name === MyBetsImages.EmptyBets) as MyBetsImage;
    showLabels = !this.emptyBetsImg;

    private readonly productHomepagesConfig = inject(ProductHomepagesConfig);

    constructor(
        public sitecoreMyBets: SitecoreMyBetsConfig,
        protected deviceService: DeviceService,
        private activeModal: ActiveModal,
        private trackingService: TrackingService,
        private redirectHelper: RedirectHelperService,
        private popupManager: PopupManager,
        private myBetsConfig: MyBetsConfig,
        private router: Router,
    ) {}

    ngOnInit(): void {
        this.isMigratedUser = this.showMigrationTabs;
        this.isMobile = this.deviceService.isMobile;
        this.isTablet = this.deviceService.isTablet;
    }

    private get isPopupOpen(): boolean {
        return this.popupManager.isPopupOpen('my-bets');
    }

    get hasFilter(): boolean {
        const { showFilter, showMigrationTabs, showPrintButton } = this.myBetsListContext;

        return !!showFilter || !!showMigrationTabs || !!showPrintButton;
    }

    emptyImgLoaded() {
        this.showLabels = true;
    }

    get emptyListMessage(): string {
        if (this.errorLoadingBets) {
            return this.sitecoreMyBets.myBets.ErrorMessage;
        }

        switch (this.listType) {
            case BetslipListType.Live:
                return this.sitecoreMyBets.myBets.NoLiveBetsAvailable;
            case BetslipListType.Open:
                return this.sitecoreMyBets.myBets.NoOpenBetsAvailable;
            case BetslipListType.Settled:
                return this.sitecoreMyBets.myBets.NoSettledBetsAvailable;
            default:
                return this.sitecoreMyBets.myBets.NoBetsALL;
        }
    }

    get emptyListSubMessage(): string {
        if (this.errorLoadingBets) {
            return '';
        }

        switch (this.listType) {
            case BetslipListType.Live:
                return this.sitecoreMyBets.myBets.LiveBetAppearHere;
            case BetslipListType.Open:
                return this.sitecoreMyBets.myBets.PlaceNewBet;
            case BetslipListType.Settled:
                return this.sitecoreMyBets.myBets.SettledBetAppearHere;
            default:
                return '';
        }
    }

    onFilterChanged(filter: MyBetsFilterConstraint): void {
        this.filterChanged.emit(filter);
    }

    browseTheSportsbookTracking(): void {
        const destinationUrl = this.productHomepagesConfig.sports.slice(0, -3) + this.router.url;
        const trackingData = {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: trackingConstants.BETTING,
            [trackingConstants.COMPONENT_LABEL_EVENT]: trackingConstants.MYBETS,
            [trackingConstants.COMPONENT_ACTION_EVENT]: trackingConstants.EVENT_CLICK,
            [trackingConstants.COMPONENT_POSITION_EVENT]: this.listType?.toLowerCase() + ' ' + trackingConstants.BETS,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: trackingConstants.MYBETS,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: this.browseSportsBook.toLowerCase(),
            [trackingConstants.COMPONENT_URL_CLICKED]: destinationUrl,
        };
        this.trackingService.track(trackingConstants.EVENT_TRACKING, trackingData);
    }

    close() {
        this.browseTheSportsbookTracking();
        if (this.isPopupOpen) {
            this.activeModal.close();
        } else {
            this.goBackToPreviousPage();
        }
    }

    // Redirect to last visited page
    goBackToPreviousPage(): void {
        this.trackingService.track(trackingConstants.EVENT_NAVIGATION_MENUS, {
            [trackingConstants.PAGE_NAVIGATION_MENUS]: `${TrackNavArea.NavBreadcrumb}_Back`,
        });
        this.redirectHelper.goBack();
    }

    get showBrowseSportsBook(): boolean {
        return this.myBetsConfig.showBrowseSportsBook && !!this.browseSportsBook;
    }

    get browseSportsBook(): string {
        return this.sitecoreMyBets.myBets.BrowseSportsBook;
    }

    get isMyBetsEmptyStateWithFilter(): boolean {
        return this.isMobile && this.emptyBetsImg && this.hasFilter;
    }
}
